/**
 * Stale-chunk recovery for the code-split SPA.
 *
 * Route chunks ship with content-hashed names (`_app-BAErcusP.js`).
 * Each deploy emits fresh hashes and Cloudflare Pages serves only the
 * latest deploy at the production domain — so a tab still running the
 * previous `index.html` requests chunk names that no longer exist. CF
 * Pages answers the miss with the SPA fallback (`index.html`,
 * `text/html`), and the browser rejects it as a module script:
 * "Failed to fetch dynamically imported module" / a MIME mismatch.
 *
 * The cure is a single reload: it pulls the fresh `index.html` plus the
 * new asset graph. We trigger it from three places — Vite's
 * `vite:preloadError` event (the common path), the React error
 * boundary, and the router's error component — all funnelling through
 * `reloadForStaleChunk()` so the guard is shared.
 *
 * Two error shapes are recognised:
 *
 *   - `isStaleChunkError` — the import literally failed (404 / MIME
 *     mismatch). Message-only match across Chrome/Firefox/Safari/Vite.
 *
 *   - `isRouterInternalError` — the imports succeeded but TanStack
 *     Router observed a partially-initialised route during preload
 *     (e.g. mid-cutover module-init race), surfacing as a generic
 *     `TypeError: Cannot read properties of undefined (reading
 *     'component')` from `vendor-tanstack-*.js`. Matched only when the
 *     stack carries the router origin so we don't reload on real
 *     in-app TypeErrors with the same shape.
 */

const RELOAD_LEDGER_KEY = 'cw:stale-chunk-reloads';
/**
 * Query param appended to the recovery reload so it lands on a fresh
 * cache key — see {@link reloadForStaleChunk}. Stripped from the URL on
 * the next boot (main.tsx) so it never lingers in the address bar.
 */
export const RELOAD_PARAM = '__cwreload';
/** Window over which attempts for one failure reason are counted. */
const RELOAD_WINDOW_MS = 10_000;
/**
 * Attempts allowed for the SAME reason inside {@link RELOAD_WINDOW_MS}.
 *
 * A Cloudflare Pages deploy *promotion* takes several seconds, during
 * which the origin serves an inconsistent asset graph. One attempt is
 * not enough: it lands back inside the same window, produces the same
 * error, and (under the old one-and-done guard) bailed straight to the
 * crash screen. Retrying a few times with {@link RETRY_BACKOFF_MS} rides
 * the window out instead. Past this count we assume real breakage — the
 * chunk is missing from the *current* deploy — and let the error surface.
 */
const MAX_ATTEMPTS_PER_REASON = 3;
/**
 * Delay before each attempt, indexed by how many attempts for this
 * reason already happened. First is immediate (the common case is a
 * merely-stale cache, which busts instantly); later ones space out to
 * let a promotion finish. Total span ≈ 4.5s of waiting plus the page
 * loads between attempts.
 */
const RETRY_BACKOFF_MS = [0, 1_500, 3_000];
/** Absolute ceiling on recovery reloads inside {@link BURST_WINDOW_MS}. */
const BURST_MAX = 5;
const BURST_WINDOW_MS = 120_000;

// Cross-browser wording for "a dynamically imported module failed to
// load": Chrome, Firefox, and Safari each phrase it differently, plus
// Vite's own preload-helper messages.
const STALE_CHUNK_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|unable to preload (css|module)/i;

export function isStaleChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? `${error.name}: ${error.message}` : String(error ?? '');
  return STALE_CHUNK_RE.test(msg);
}

// "Undefined property access" phrasing across engines.
//   - V8/Chrome:  "Cannot read properties of undefined (reading 'x')"
//   - Older V8:   "Cannot read property 'x' of undefined"
//   - JSC/Safari: "undefined is not an object (evaluating 'a.x')"
//   - SpiderMonkey/Firefox: "a is undefined"
const UNDEFINED_ACCESS_RE =
  /cannot read properties of undefined|cannot read property\s+'[^']*'\s+of undefined|undefined is not an object|\bis undefined\b/i;

// Stack-origin markers for TanStack Router internals. Production bundle
// emits the vendor as `vendor-tanstack-<hash>.js` (see vite.config.ts
// manualChunks); dev mode resolves to the package paths.
const ROUTER_ORIGIN_RE =
  /vendor-tanstack[-.][a-z0-9]+\.m?js|@tanstack[\\/]+(?:react-)?router(?:-core)?[\\/]+/i;

/**
 * Match TanStack Router's internal "preload observed an undefined
 * route option" failure mode — the one that surfaces as
 * `Cannot read properties of undefined (reading 'component')` from
 * `preloadRouteComponents` in `vendor-tanstack-*.js` during a deploy
 * cutover. Requires BOTH:
 *
 *   1. Undefined-property-access shape on the message, AND
 *   2. Stack frames pointing at the router vendor chunk / package.
 *
 * The stack-origin gate is what makes this safe to reload on:
 * generic `TypeError: cannot read properties of undefined` from
 * application code does NOT match, so we won't loop-reload over a
 * real bug.
 *
 * Non-Error inputs always return false — we need a stack to gate on.
 */
export function isRouterInternalError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = `${error.name}: ${error.message}`;
  if (!UNDEFINED_ACCESS_RE.test(msg)) return false;
  const stack = error.stack ?? '';
  return ROUTER_ORIGIN_RE.test(stack);
}

interface ReloadEntry {
  /** Epoch ms of the reload. */
  t: number;
  /** Failure reason (error message / chunk URL) the reload recovered from. */
  r: string;
}

function readLedger(): ReloadEntry[] {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_LEDGER_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is ReloadEntry =>
        typeof e === 'object' && e !== null && typeof (e as ReloadEntry).t === 'number',
    );
  } catch {
    /* sessionStorage blocked (privacy mode) or corrupted — treat as empty */
    return [];
  }
}

/**
 * Reload to recover from a stale chunk, guarded three ways:
 *
 *   1. Per reason, with retries — up to
 *      {@link MAX_ATTEMPTS_PER_REASON} attempts for the SAME failure
 *      inside {@link RELOAD_WINDOW_MS}, spaced by
 *      {@link RETRY_BACKOFF_MS}. A deploy promotion serves an
 *      inconsistent asset graph for several seconds, so a single
 *      attempt lands back in the same window and fails identically;
 *      retrying rides it out. Past the cap the chunk is genuinely
 *      missing from the current deploy — surface the error.
 *   2. A DIFFERENT failure gets its own attempt budget, so back-to-back
 *      deploys (each breaking a different chunk) recover instead of
 *      exhausting one shared budget — the gap that put the crash screen
 *      up during the 2026-07-10 triple deploy.
 *   3. Burst ceiling — at most {@link BURST_MAX} recovery reloads per
 *      {@link BURST_WINDOW_MS} regardless of reason, so a pathological
 *      stream of novel errors still can't loop the tab.
 *
 * Returns true if a reload was scheduled (the first attempt navigates
 * synchronously; retries are deferred by the backoff).
 */
export function reloadForStaleChunk(reason = ''): boolean {
  if (typeof window === 'undefined') return false;
  const now = Date.now();
  const ledger = readLedger().filter((e) => now - e.t < BURST_WINDOW_MS);
  const attemptsForReason = ledger.filter(
    (e) => e.r === reason && now - e.t < RELOAD_WINDOW_MS,
  ).length;
  if (attemptsForReason >= MAX_ATTEMPTS_PER_REASON) return false;
  if (ledger.length >= BURST_MAX) return false;
  try {
    window.sessionStorage.setItem(
      RELOAD_LEDGER_KEY,
      JSON.stringify([...ledger, { t: now, r: reason }]),
    );
  } catch {
    /* ignore — reload anyway; worst case the next failure reloads too */
  }
  // Cache-BUSTING reload, not a plain reload(). A fresh query key forces
  // both the browser and the Cloudflare edge (whose SPA-document cache
  // rule holds the last-good shell for ~30s after a deploy — see
  // infra/pulumi cloudflare.ts) to fetch a fresh index.html + asset
  // graph. A bare reload() would re-serve the SAME stale shell that just
  // failed, re-trip the per-reason guard, and surface the crash screen.
  // The param is stripped on the next boot (main.tsx) so it never sticks.
  const url = new URL(window.location.href);
  url.searchParams.set(RELOAD_PARAM, String(now));
  const target = url.toString();

  // First attempt navigates immediately — a merely-stale cache busts on
  // the spot. Later attempts back off so a multi-second deploy promotion
  // has time to finish rather than us re-landing mid-cutover.
  const delay = RETRY_BACKOFF_MS[Math.min(attemptsForReason, RETRY_BACKOFF_MS.length - 1)] ?? 0;
  if (delay > 0) {
    window.setTimeout(() => window.location.replace(target), delay);
  } else {
    window.location.replace(target);
  }
  return true;
}

/** Failure-reason string for the guard — message-shaped, never throws. */
export function staleChunkReason(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error ?? '');
}
